// @ts-nocheck
/**
 * Accordion Drawer Component
 * Handles the tabbed drawer functionality for accordion rows with open_in_drawer setting
 */
class AccordionDrawerComponent extends HTMLElement {
  #controller = new AbortController();
  #drawerRows = [];
  #isInitialized = false;
  #indicator = null;

  connectedCallback() {
    this.#initializeDrawer();
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  #initializeDrawer() {
    if (this.#isInitialized) return;

    const { signal } = this.#controller;

    // Collect all drawer rows
    this.#drawerRows = Array.from(this.querySelectorAll('[data-drawer-row-id]'));

    if (this.#drawerRows.length === 0) return;

    // Build the drawer content
    this.#buildDrawerContent();

    // Set up trigger handlers for drawer trigger elements
    this.querySelectorAll('[data-open-drawer-tab]').forEach((trigger) => {
      trigger.addEventListener('click', this.#handleTriggerClick.bind(this), { signal });
    });

    // Set up tab switching
    const dialogComponent = this.querySelector('dialog-component');
    if (dialogComponent) {
      this.#setupTabSwitching(dialogComponent, signal);

      // Videos that autoplay on open stop when the drawer closes
      dialogComponent.addEventListener('dialog:close', () => this.#syncTabMedia(), { signal });
    }

    // Keep the indicator in sync when the viewport changes
    window.addEventListener('resize', () => this.#updateIndicator(), { signal });

    this.#isInitialized = true;
  }

  #buildDrawerContent() {
    const tabsContainer = this.querySelector('.accordion-drawer__tabs');
    const contentContainer = this.querySelector('.accordion-drawer__content');

    if (!tabsContainer || !contentContainer) return;

    // Show tabs only if there are multiple drawer rows
    if (this.#drawerRows.length > 1) {
      tabsContainer.hidden = false;

      // Create sliding indicator
      let indicator = tabsContainer.querySelector('.accordion-drawer__tab-indicator');
      if (!indicator) {
        indicator = document.createElement('div');
        indicator.className = 'accordion-drawer__tab-indicator';
        tabsContainer.appendChild(indicator);
      }
      this.#indicator = indicator;
    }

    this.#drawerRows.forEach((row, index) => {
      const rowId = row.dataset.drawerRowId;
      const heading = row.dataset.drawerRowHeading;
      const hiddenContent = row.querySelector('.accordion-drawer-row-content');

      if (!hiddenContent) return;

      // Create tab button (only if multiple rows)
      if (this.#drawerRows.length > 1) {
        const tab = document.createElement('button');
        tab.className = `button accordion-drawer__tab${index === 0 ? ' accordion-drawer__tab--active' : ''}`;
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
        tab.setAttribute('aria-controls', `accordion-drawer-panel-${rowId}`);
        tab.setAttribute('id', `accordion-drawer-tab-${rowId}`);
        tab.setAttribute('type', 'button');
        tab.textContent = heading;
        tabsContainer.appendChild(tab);
      }

      // Create panel
      const panel = document.createElement('div');
      panel.className = `accordion-drawer__panel gap-style${index === 0 ? ' accordion-drawer__panel--active' : ''}`;
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('id', `accordion-drawer-panel-${rowId}`);
      panel.setAttribute('aria-labelledby', `accordion-drawer-tab-${rowId}`);
      if (index !== 0) panel.hidden = true;

      // Clone the content into the panel
      panel.innerHTML = hiddenContent.innerHTML;

      // Copy gap CSS variables from hidden content to panel
      const hiddenStyle = hiddenContent.getAttribute('style');
      if (hiddenStyle) {
        panel.setAttribute('style', hiddenStyle);
      }

      contentContainer.appendChild(panel);
    });
  }

  #handleTriggerClick(event) {
    const trigger = event.currentTarget;
    const tabId = trigger.dataset.openDrawerTab;
    const dialogComponent = this.querySelector('dialog-component');

    if (!dialogComponent) return;

    this.activateTab(tabId);

    dialogComponent.showDialog();
    this.#syncTabMedia();

    // Re-measure once the dialog is laid out (avoids 0-width on first open)
    requestAnimationFrame(() => this.#updateIndicator());
  }

  activateTab(tabId) {
    const tabs = this.querySelectorAll('.accordion-drawer__tab');
    const panels = this.querySelectorAll('.accordion-drawer__panel');

    tabs.forEach((tab) => {
      const isActive = tab.getAttribute('aria-controls') === `accordion-drawer-panel-${tabId}`;
      tab.classList.toggle('accordion-drawer__tab--active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    panels.forEach((panel) => {
      const isActive = panel.id === `accordion-drawer-panel-${tabId}`;
      panel.classList.toggle('accordion-drawer__panel--active', isActive);
      panel.hidden = !isActive;
    });

    this.#updateIndicator();
    this.#syncTabMedia();
  }

  /**
   * Plays the `data-autoplay-on-open` videos of the active panel while the drawer is open
   * (muted, so the browser allows it), and pauses the others.
   */
  #syncTabMedia() {
    const isOpen = Boolean(this.querySelector('dialog-component dialog')?.open);

    this.querySelectorAll('.accordion-drawer__panel').forEach((panel) => {
      const shouldPlay = isOpen && !panel.hidden;

      panel.querySelectorAll('deferred-media[data-autoplay-on-open]').forEach((media) => {
        if (shouldPlay) {
          // Marks the video as a muted decorative loop: playing the next video of the panel
          // (mediaStartedPlaying) must not pause this one.
          media.setAttribute('autoplay', '');
          media.autoplayMedia?.();
        } else if (media.hasAttribute('data-media-loaded')) {
          media.pauseMedia?.();
        }
      });
    });
  }

  #updateIndicator() {
    if (!this.#indicator) return;

    const tabsContainer = this.querySelector('.accordion-drawer__tabs');
    const activeTab = tabsContainer?.querySelector('.accordion-drawer__tab--active');

    if (!tabsContainer || !activeTab) return;

    const containerRect = tabsContainer.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    const left = tabRect.left - containerRect.left + tabsContainer.scrollLeft;
    const width = tabRect.width;

    this.#indicator.style.left = `${left}px`;
    this.#indicator.style.width = `${width}px`;
  }

  #setupTabSwitching(dialogComponent, signal) {
    dialogComponent.addEventListener(
      'click',
      (event) => {
        const tab = event.target.closest('.accordion-drawer__tab');
        if (!tab) return;

        const controlsId = tab.getAttribute('aria-controls');
        if (controlsId) {
          const tabId = controlsId.replace('accordion-drawer-panel-', '');
          this.activateTab(tabId);
        }
      },
      { signal }
    );

    // Keyboard navigation
    dialogComponent.addEventListener(
      'keydown',
      (event) => {
        const tab = event.target.closest('.accordion-drawer__tab');
        if (!tab) return;

        const tabs = Array.from(this.querySelectorAll('.accordion-drawer__tab'));
        const currentIndex = tabs.indexOf(tab);
        let newIndex = currentIndex;

        switch (event.key) {
          case 'ArrowLeft':
            newIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
            break;
          case 'ArrowRight':
            newIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
            break;
          case 'Home':
            newIndex = 0;
            break;
          case 'End':
            newIndex = tabs.length - 1;
            break;
          default:
            return;
        }

        event.preventDefault();
        tabs[newIndex].focus();
        tabs[newIndex].click();
      },
      { signal }
    );
  }
}

if (!customElements.get('accordion-drawer-component')) {
  customElements.define('accordion-drawer-component', AccordionDrawerComponent);
}

